import { useDispatch, useSelector } from 'react-redux';
import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
  putMultipleFundingSourceForClientByClientIdRequest,
} from '@client/model';
import { SessionType, getPolicyTypeFromTraditionalLTCQuote } from '@shared';
import { parseBioPercentToInflationRate } from '@client/util/policyQuoteCalculations';

interface UseTraditionalLTCPolicyManagementProps {
  quote: TraditionalLTCQuote;
  fundingSourceId?: string | null;
  onClose?: (extraParams?: Record<string, string>) => void;
}

export function useTraditionalLTCPolicyManagement({
  quote,
  fundingSourceId = null,
  onClose,
}: UseTraditionalLTCPolicyManagementProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const getPremiumCostPerMonth = (quote: TraditionalLTCQuote): number => {
    switch (quote.premiumFrequency) {
      case 'Monthly':
        return quote.primaryModalPremium;
      case 'Annually':
        return quote.primaryModalPremium / 12;
      default:
        return quote.primaryModalPremium / 12;
    }
  };

  const handleAddPolicy = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!client) return;

    const inflationRate = parseBioPercentToInflationRate(quote.bioPercent);

    const basicPolicy = {
      carrierName: quote.carrier,
      hasPolicyFunding: true,
      isSimpleInflationProtection: false,
      addedByAdvisorId: isAdvisorSession ? advisor?.advisorId : undefined,
      isIndemnityPolicyPayment: false,
      policyBenefitIsUnlimited: false,
      policyMaximumBenefitAmount: quote.benefitPool,
      policyPremiumMonthlyCost: getPremiumCostPerMonth(quote),
      policyPremiumStartYear: new Date().getFullYear(),
      policyInflationProtection: inflationRate,
      policyLumpSumPayment: null,
      dailyHomeBenefitAmount: quote.dailyBenefitAmount,
      dailyHomeInflationProtectionPercent: null,
      dailyIndependentLivingBenefitAmount: quote.dailyBenefitAmount,
      dailyIndependentLivingInflationProtectionPercent: null,
      dailyAssistedLivingBenefitAmount: quote.dailyBenefitAmount,
      dailyAssistedLivingInflationProtectionPercent: null,
      dailyNursingHomeBenefitAmount: quote.dailyBenefitAmount,
      dailyNursingHomeInflationProtectionPercent: null,
      homeCareWaitingPeriodDays: quote.eliminationPeriod,
      facilityCareWaitingPeriodDays: quote.eliminationPeriod,
      policyLimitedPayYears: null,
      policyBenefitPeriodMonths: null,
      policyMonthlyWithdrawalRate: null,
      policyPremiumIncreaseRate: null,
      policyPremiumIncreaseYears: null,
      policyInflationProtectionOnFaceAmount: null,
      policyInflationProtectionOnCOBAmount: null,
      policyInflationProtectionYears: null,
      policyBenefitUtilizedToDate: 0,
      policyContinuationBenefitAmount: null,
      policyMinimumGuaranteedBenefitAmount: null,
      customPolicyId: quote.traditionalLTCQuoteId,
      addedViaShopInsurance: false,
      insuranceQuoteId: quote.traditionalLTCQuoteId,
      sourceQuote: quote,
      policyType: getPolicyTypeFromTraditionalLTCQuote(quote),
    };

    const finalPolicy = basicPolicy;

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: finalPolicy,
        fundingSourceId: null,
      }),
    );

    if (onClose) {
      const url = new URL(window.location.href);
      url.searchParams.set('downloadReport', 'true');
      window.history.replaceState({}, '', url.toString());
      onClose({ downloadReport: 'true' });
    }
  };

  const handleRemovePolicy = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!client || !fundingSourceId) return;

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: null,
        fundingSourceId,
      }),
    );
  };

  return {
    handleAddPolicy,
    handleRemovePolicy,
  };
}
